import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getDashboardActionItems,
  getDashboardCashFlow
} from '../api/dashboard';
import * as appointmentsApi from '../api/appointments';
import * as opportunitiesApi from '../api/opportunities';
import * as clientsApi from '../api/clients';
import * as quotesApi from '../api/quotes';
import CashFlow from '../components/CashFlow';
import AppointmentsSection from '../components/dashboard/AppointmentsSection';
import PendingQuotesSection from '../components/dashboard/PendingQuotesSection';
import InvoicesSection from '../components/dashboard/InvoicesSection';
import WelcomeBanner from '../components/onboarding/WelcomeBanner';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import ErrorMessage from '../components/ErrorMessage';
import MessageModal from '../components/MessageModal';
import moment from 'moment';

// Utility functions
const parseLocalDateTime = (dateTimeString) => {
  if (!dateTimeString) return new Date();
  const normalized = dateTimeString.replace(' ', 'T');
  const [datePart, timePart] = normalized.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = (timePart || '00:00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
};

const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// New Appointment Modal Component
const NewAppointmentModal = ({ appointment, opportunities, clients, onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    title: appointment?.title || 'Estimate',
    description: appointment?.description || '',
    startDatetime: appointment?.start_datetime || '',
    duration: 60,
    location: appointment?.location || '',
    status: appointment?.status || 'scheduled',
    opportunityId: appointment?.opportunityId || '',
    clientId: appointment?.clientId || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newlyCreatedAppointment, setNewlyCreatedAppointment] = useState(null);

  useEffect(() => {
    if (appointment && !formData.startDatetime) {
      let duration = 60;
      if (appointment.start_datetime && appointment.end_datetime) {
        const startTime = parseLocalDateTime(appointment.start_datetime);
        const endTime = parseLocalDateTime(appointment.end_datetime);
        duration = Math.round((endTime - startTime) / (1000 * 60));
      }

      setFormData(prev => ({
        ...prev,
        startDatetime: appointment.start_datetime ?
          appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '',
        duration: duration || 60
      }));
    }
  }, [appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const startDate = parseLocalDateTime(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000));

      const appointmentData = {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: formatLocalDateTime(endDate),
        location: formData.location,
        status: formData.status,
        opportunityId: formData.opportunityId || null,
        clientId: formData.clientId || null
      };

      const response = await appointmentsApi.createAppointment(appointmentData);
      setNewlyCreatedAppointment(response.appointment);

      if (formData.opportunityId) {
        try {
          await opportunitiesApi.updateOpportunityStatus(formData.opportunityId, 'appointment_scheduled');
        } catch (statusError) {
          console.error('Warning: Failed to update opportunity status:', statusError);
        }
      }

      setShowEmailModal(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));
  const selectedOpportunity = opportunities.find(opp => opp.id === parseInt(formData.opportunityId));

  return (
    <Modal onClose={onClose} title="New Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        <div>
          <label className="block text-sm font-medium text-gray-700">Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.startDatetime}
              onChange={(e) => setFormData({...formData, startDatetime: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duration *</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
        </div>

        {formData.startDatetime && (() => {
          const startDate = parseLocalDateTime(formData.startDatetime);
          const endDate = new Date(startDate.getTime() + (formData.duration * 60000));
          return (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-600">
                <strong>Appointment will end at:</strong> {' '}
                {endDate.toLocaleString()}
              </p>
            </div>
          );
        })()}

        <div>
          <label className="block text-sm font-medium text-gray-700">Client (Optional)</label>
          <select
            value={formData.clientId}
            onChange={(e) => {
              const clientId = e.target.value;
              const selectedClient = clients.find(c => c.id === parseInt(clientId));
              setFormData({
                ...formData,
                clientId: clientId,
                location: selectedClient?.address || formData.location
              });
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select a client (optional)</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name} {client.email ? `(${client.email})` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">Select a client for general appointments not tied to a specific project</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rescheduled">Rescheduled</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            rows="3"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Appointment'}
          </button>
        </div>
      </form>

      {showEmailModal && newlyCreatedAppointment && selectedClient && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            onSuccess('Appointment created successfully!');
          }}
          templateType="appointment_scheduled"
          recipientEmail={selectedClient.email || ''}
          contextData={{
            client_name: selectedClient.name || '',
            appointment_title: newlyCreatedAppointment.title || '',
            appointment_date: moment(newlyCreatedAppointment.start_datetime).format('MMMM D, YYYY'),
            appointment_time: moment(newlyCreatedAppointment.start_datetime).format('h:mm A'),
            appointment_location: newlyCreatedAppointment.location || '',
            ...(selectedOpportunity?.project_name && { project_name: selectedOpportunity.project_name }),
            company_name: user?.companyName || user?.username || ''
          }}
          opportunityId={newlyCreatedAppointment.opportunity_id || null}
        />
      )}
    </Modal>
  );
};

function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [cashFlow, setCashFlow] = useState({
    potentialRevenue: 0,
    invoicedRevenue: 0,
    paidRevenue: 0
  });
  const [actionItems, setActionItems] = useState({
    appointments: [],
    pendingQuotes: [],
    invoices: []
  });
  const [clients, setClients] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (authLoading) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        setLoading(true);

        // Fetch cash flow, action items, appointments, clients, opportunities, and quotes in parallel
        const [cashFlowData, actionItemsData, appointmentsData, clientsData, opportunitiesData, quotesData] = await Promise.allSettled([
          getDashboardCashFlow(),
          getDashboardActionItems(),
          appointmentsApi.fetchAppointments(),
          clientsApi.fetchClients(),
          opportunitiesApi.getOpportunities(),
          quotesApi.fetchQuotes()
        ]);

        // Extract cash flow data
        const cashFlowResult = cashFlowData.status === 'fulfilled' ? cashFlowData.value.cashFlow : {
          potentialRevenue: 0,
          invoicedRevenue: 0,
          paidRevenue: 0
        };
        setCashFlow(cashFlowResult);

        // Extract quotes data for enriching pending quotes
        const allQuotes = quotesData.status === 'fulfilled' ? quotesData.value.quotes || [] : [];
        const quoteMap = new Map(allQuotes.map(q => [q.id, q]));
        const clientsResult = clientsData.status === 'fulfilled' ? clientsData.value.clients || [] : [];
        const clientMap = new Map(clientsResult.map(c => [c.id, c]));

        // Extract and transform action items
        if (actionItemsData.status === 'fulfilled') {
          const items = actionItemsData.value.actionItems;

          // Transform pending quotes (recently sent quotes)
          const pendingQuotes = items.expiringSoon?.map((quote) => {
            // Extract client name from description if available
            const descParts = quote.description?.split(' - ') || [];
            const clientName = descParts[0] || quote.title;
            const amount = quote.grand_total || 0;

            // Extract days ago from subtitle (e.g., "Sent 3 days ago" -> 3)
            const daysAgoMatch = quote.subtitle?.match(/(\d+)\s+days?\s+ago/) || [];
            const daysAgo = daysAgoMatch[1] ? parseInt(daysAgoMatch[1]) : 0;

            // Get the full quote data to retrieve publicAccessToken
            const fullQuote = quoteMap.get(quote.id);

            // Get client email from the quote's client_id
            const quoteClient = fullQuote?.clientId ? clientMap.get(fullQuote.clientId) : null;
            const clientEmail = quoteClient?.email || fullQuote?.clientEmail || '';

            console.log('Dashboard: Processing quote', quote.id, {
              fullQuote,
              quoteClient,
              clientEmail,
              clientId: fullQuote?.clientId
            });

            return {
              id: quote.id,
              clientName,
              projectName: quote.title,
              amount,
              daysAgo,
              publicAccessToken: fullQuote?.publicAccessToken || '',
              clientEmail: clientEmail
            };
          }) || [];

          // Transform invoices (combine upcoming and overdue)
          const invoices = [
            ...(items.overdueInvoices?.map((inv) => {
              // Extract client name from description
              const descParts = inv.description?.split(' - ') || [];
              const clientName = descParts[0] || inv.title;
              const amount = inv.total || 0;

              return {
                id: inv.id,
                clientName,
                projectName: inv.title,
                amount,
                status: 'overdue',
                daysOverdue: 5 // Default - will need to parse from subtitle if needed
              };
            }) || [])
          ];

          // Transform appointments
          const appointments = appointmentsData.status === 'fulfilled'
            ? (appointmentsData.value.appointments || []).map((apt) => {
                // Parse the start_datetime to extract date and time
                const startDate = new Date(apt.start_datetime);
                const dateStr = startDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
                const timeStr = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

                return {
                  id: apt.id,
                  title: apt.title || 'Untitled',
                  clientName: apt.client_name || 'Unknown Client',
                  date: dateStr,
                  time: timeStr,
                  location: apt.location || '',
                  description: apt.description || '',
                  phone: apt.phone || '',
                  email: apt.email || ''
                };
              })
            : [];

          setActionItems({
            appointments,
            pendingQuotes,
            invoices
          });
        }

        // Set opportunities
        const opportunitiesResult = opportunitiesData.status === 'fulfilled' ? opportunitiesData.value.opportunities || [] : [];
        setClients(clientsResult);
        setOpportunities(opportunitiesResult);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center text-lg text-gray-700">
        Loading application...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {user && user.onboardingStatus === 'not_started' ? (
          <WelcomeBanner />
        ) : (
          <>
            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
              <PageHeader title="Dashboard" />
              {user && (
                <p className="text-gray-600 mt-4">
                  👋 Welcome back, <span className="font-semibold">{user.username}</span>
                  {user.company_name && (
                    <span> · {user.company_name}</span>
                  )}
                </p>
              )}
            </div>

            {/* Cash Flow Section */}
            {!loading && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  💵 Month-to-Date Cash Flow
                </h2>
                <CashFlow cashFlow={cashFlow} loading={loading} />
              </div>
            )}

            {/* Action Items - Collapsible Sections */}
            <div className="space-y-4">
              {/* Appointments Section */}
              <AppointmentsSection
                appointments={actionItems.appointments}
                loading={loading}
                clients={clients}
                opportunities={opportunities}
                user={user}
              />

              {/* Pending Quotes Section */}
              <PendingQuotesSection
                quotes={actionItems.pendingQuotes}
                loading={loading}
              />

              {/* Invoices Section */}
              <InvoicesSection
                invoices={actionItems.invoices}
                loading={loading}
              />
            </div>

            {/* Quick Actions Bar */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                ⚡ Quick Actions
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Link
                  to="/quotes/new"
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors text-center"
                >
                  + New Quote
                </Link>
                <Link
                  to="/invoices/new"
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  + New Invoice
                </Link>
                <Link
                  to="/clients/new"
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  + Add Client
                </Link>
                <button
                  onClick={() => setShowNewAppointmentModal(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors text-center"
                >
                  + Appointment
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New Appointment Modal */}
      {showNewAppointmentModal && (
        <NewAppointmentModal
          appointment={null}
          opportunities={opportunities}
          clients={clients}
          onClose={() => setShowNewAppointmentModal(false)}
          onSuccess={() => {
            setShowNewAppointmentModal(false);
            // Refresh dashboard data
            window.location.reload();
          }}
          user={user}
        />
      )}
    </div>
  );
}

export default DashboardPage;